'use client';

// imports
import { useState, useEffect, useCallback } from "react";
import useInterval from "@/lib/hooks/useInterval";
import useDidMount from '@/lib/hooks/useDidMount';
import Cookies from "js-cookie";

// lib files
import { Channel, LivestreamField, StationResource } from "@/lib/types/api/multi-livestream";
import { LIVESTREAM_SCHEDULE_FETCH_INTERVAL } from "@/lib/constants";

// components
import MultiLivestreamPlayer from "@/components/MultiLivestream/MultiLivestreamPlayer";
import MultiLivestreamStationInfo from "@/components/MultiLivestream/MultiLivestreamStationInfo";
import MultiLivestreamSchedule from "@/components/MultiLivestream/Schedule/Schedule";
import MultiLivestreamError from "@/components/MultiLivestream/MultiLivestreamError";
import PageTransition from "@/components/Transitions/PageTransition";

// styles
import styles from '@/components/MultiLivestream/MultiLivestream.module.scss';

// defining the possible profile names
const channels = ['ga-main', 'ga-world', 'ga-create', 'ga-fnx', 'ga-nhk', 'ga-local-subchannel-1', 'ga-local-subchannel-2'
// @TODO in future there may be other sub channels which would need to be added here
];
interface MultiLivestreamProps {
  basePlayerUrl: string;
  stationName: string;
  scheduleUrl: string;
  stationId: string;
}
function MultiLivestream(props: MultiLivestreamProps) {
  const {
    basePlayerUrl,
    stationName,
    scheduleUrl,
    stationId
  } = props;
  const [stationData, setStationData] = useState<StationResource>();
  const [scheduleData, setScheduleData] = useState<Channel[]>();
  const [dataFetchError, setDataFetchError] = useState(false);

  // @TODO: update to human-readable profile slug, if/when available from CS
  const [selectedChannel, setSelectedChannel] = useState('');
  const [userSelectedChannel, setUserSelectedChannel] = useState('');
  const fetchStationData = useCallback(async () => {
    try {
      const cookieOrStationId = Cookies.get('pbsol.station_id') || stationId;
      const response = await fetch(`/api/station/${cookieOrStationId}/data/`);
      const json = await response.json();
      setStationData(json.stationData);
    } catch (error) {
      console.error({
        error
      });
      setDataFetchError(true);
    }
  }, [stationId]);
  const fetchScheduleData = useCallback(async () => {
    try {
      const cookieOrStationId = Cookies.get('pbsol.station_id') || stationId;
      const response = await fetch(`/api/station/${cookieOrStationId}/livestream/`);
      const json = await response.json();
      setScheduleData(json.schedule.content[0]?.channels);
      setSelectedChannel(json.schedule.content[0]?.channels[0].feed_cid);
    } catch (error) {
      console.log({
        error
      });
      setDataFetchError(true);
    }
  }, [stationId]);
  useDidMount(fetchStationData);
  useDidMount(fetchScheduleData);
  useInterval(fetchScheduleData, LIVESTREAM_SCHEDULE_FETCH_INTERVAL);

  // when we have data, check query params for a pre-selected livestream profile;
  // if one is found, set the selected channel.
  // this allows us to link directly to a particular channel, e.g.
  // pbs.org/livestream/?selected=ga-world
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const profile = queryParams.get('selected');
    if (profile && channels.includes(profile) && stationData && scheduleData) {
      const livestreamFeeds = stationData.livestream_fields;
      const selectedFeed = livestreamFeeds.find((livestream: LivestreamField) => livestream.profile === profile);
      if (!selectedFeed) return;
      const livestreamCid = selectedFeed.feed_cid;
      if (!livestreamCid) return;
      setSelectedChannel(livestreamCid);
      setUserSelectedChannel(livestreamCid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationData, scheduleData]);
  return <PageTransition data-sentry-element="PageTransition" data-sentry-component="MultiLivestream" data-sentry-source-file="MultiLivestream.tsx">
      <div className={styles.multi_livestream__root}>
        {scheduleData && stationData && <section className={styles.multi_livestream__app}>
          <MultiLivestreamPlayer basePlayerUrl={basePlayerUrl} selectedChannel={dataFetchError ? 'default' : selectedChannel} userSelectedChannel={userSelectedChannel} />
          <MultiLivestreamStationInfo scheduleUrl={scheduleUrl} stationName={stationData.short_common_name} primaryTimezone={stationData.timezone} secondaryTimezone={stationData.secondary_timezone} />
          {dataFetchError ? <MultiLivestreamError stationName={stationName} scheduleUrl={scheduleUrl} /> : <MultiLivestreamSchedule selectedChannel={userSelectedChannel || selectedChannel} setUserSelectedChannel={setUserSelectedChannel} scheduleData={scheduleData} primaryTimezone={stationData?.timezone} secondaryTimezone={stationData?.secondary_timezone} />}
        </section>}
      </div>
    </PageTransition>;
}
export default MultiLivestream;